import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { Stream as StreamType, StreamTab, Task } from './types';
import { createApi } from './api';
import { useAuth } from './contexts/AuthContext';
import Stream from './components/Stream';
import StreamForm from './components/StreamForm';
import LoginPage from './components/LoginPage';
import UserProfile from './components/UserProfile';
import AdminPage from './components/AdminPage';

function MainContent() {
  const { user, loading: authLoading, getIdToken } = useAuth();
  const location = useLocation();

  // Hardcoded admin user ID
  const ADMIN_USER_ID = 'Y0LK1yKFX2UyFibDYedA4vm0rwF3';
  const isAdmin = user?.uid === ADMIN_USER_ID;
  const [streams, setStreams] = useState<StreamType[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStreamForm, setShowStreamForm] = useState(false);
  const [editingStream, setEditingStream] = useState<StreamType | null>(null);
  const [filterBacklog, setFilterBacklog] = useState(false);
  const [filterInProgress, setFilterInProgress] = useState(false);
  const [activeTab, setActiveTab] = useState<StreamTab | 'unassigned'>(() => {
    const saved = localStorage.getItem('taskmaster-active-tab');
    if (saved === 'work' || saved === 'personal' || saved === 'unassigned') return saved;
    return 'work';
  });

  useEffect(() => {
    localStorage.setItem('taskmaster-active-tab', activeTab as string);
  }, [activeTab]);

  useEffect(() => {
    if (user && !authLoading) {
      loadData();
    }
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      
      const [streamsData, tasksData] = await Promise.all([
        api.getStreams(),
        api.getTasks()
      ]);
      setStreams(streamsData);
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (taskData: Partial<Task>) => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      const newTask = await api.createTask(taskData);
      setTasks(prev => [...prev, newTask]);
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleCreateStream = async (streamData: { name: string; color: string; tab: StreamTab }) => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      const newStream = await api.createStream(streamData);
      setStreams(prev => [...prev, newStream]);
      setShowStreamForm(false);
    } catch (error) {
      console.error('Failed to create stream:', error);
    }
  };

  const handleEditStream = (stream: StreamType) => {
    setEditingStream(stream);
    setShowStreamForm(true);
  };

  const handleUpdateStream = async (streamData: { name: string; color: string; tab: StreamTab }) => {
    if (!editingStream) return;
    
    try {
      const token = await getIdToken();
      const api = createApi(token);
      const updatedStream = await api.updateStream(editingStream.id, streamData);
      setStreams(prev => prev.map(s => s.id === editingStream.id ? updatedStream : s));
      setShowStreamForm(false);
      setEditingStream(null);
    } catch (error) {
      console.error('Failed to update stream:', error);
    }
  };

  const handleCloseStreamForm = () => {
    setShowStreamForm(false);
    setEditingStream(null);
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const task = tasks.find(t => t.id === draggableId);
    if (!task) return;

    // Update task position locally first
    const updatedTasks = [...tasks];
    const taskIndex = updatedTasks.findIndex(t => t.id === draggableId);
    updatedTasks[taskIndex] = {
      ...task,
      phaseId: destination.droppableId
    };
    setTasks(updatedTasks);

    try {
      const token = await getIdToken();
      const api = createApi(token);
      await api.updateTask(draggableId, { phaseId: destination.droppableId });
    } catch (error) {
      console.error('Failed to update task:', error);
      // Revert on error
      setTasks(tasks);
    }
  };

  const handleClearDoneTasks = async () => {
    if (window.confirm('Are you sure you want to clear all done tasks?')) {
      try {
        const token = await getIdToken();
        const api = createApi(token);
        await api.clearDoneTasks();
        await loadData(); // Reload to get updated data
      } catch (error) {
        console.error('Failed to clear done tasks:', error);
      }
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      const updatedTask = await api.updateTask(taskId, updates);
      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, ...updatedTask } : task
      ));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      await api.deleteTask(taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleDeleteStream = async (streamId: string) => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      const result = await api.deleteStream(streamId);
      
      // Remove stream and its tasks from local state
      setStreams(prev => prev.filter(stream => stream.id !== streamId));
      setTasks(prev => prev.filter(task => task.streamId !== streamId));
      
      // Show feedback about deleted tasks
      if (result.deletedTaskCount > 0) {
        alert(`Stream deleted along with ${result.deletedTaskCount} task${result.deletedTaskCount === 1 ? '' : 's'}.`);
      }
    } catch (error) {
      console.error('Failed to delete stream:', error);
      alert('Failed to delete stream. Please try again.');
    }
  };

  if (authLoading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  // Admin route
  if (location.pathname === '/admin') {
    return <AdminPage />;
  }

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading tasks...</div>;
  }

  const streamMatchesTab = (stream: StreamType) => {
    const streamTab = stream.tab ?? null;
    if (activeTab === 'unassigned') return streamTab === null;
    return streamTab === activeTab;
  };

  const tabCounts = {
    work: streams.filter(s => (s.tab ?? null) === 'work').length,
    personal: streams.filter(s => (s.tab ?? null) === 'personal').length,
    unassigned: streams.filter(s => (s.tab ?? null) === null).length,
  };

  const filteredStreams = streams.filter(stream => {
    if (!streamMatchesTab(stream)) return false;
    if (!filterBacklog && !filterInProgress) return true;
    const streamTasks = tasks.filter(t => t.streamId === stream.id);
    if (filterBacklog) {
      const phase = stream.phases.find(p => p.name.toLowerCase() === 'to do');
      if (phase && streamTasks.some(t => t.phaseId === phase.id)) return true;
    }
    if (filterInProgress) {
      const phase = stream.phases.find(p => p.name.toLowerCase() === 'in progress');
      if (phase && streamTasks.some(t => t.phaseId === phase.id)) return true;
    }
    return false;
  });

  return (
    <div className="kanban-board">
      <div className="board-header">
        <h1 className="board-title">TaskMaster Kanban</h1>
        <div className="header-actions">
          <UserProfile />
          {isAdmin && (
            <button
              className="admin-btn"
              onClick={() => window.location.href = '/admin'}
            >
              Admin
            </button>
          )}
          <button
            className="add-stream-btn"
            onClick={() => setShowStreamForm(true)}
          >
            + Add Stream
          </button>
          <div className="filter-controls">
            <label className="filter-label">
              <input
                type="checkbox"
                checked={filterBacklog}
                onChange={e => setFilterBacklog(e.target.checked)}
                className="filter-checkbox"
              />
              Has Backlog
            </label>
            <label className="filter-label">
              <input
                type="checkbox"
                checked={filterInProgress}
                onChange={e => setFilterInProgress(e.target.checked)}
                className="filter-checkbox"
              />
              In Progress
            </label>
          </div>
          <button
            className="clear-done-btn"
            onClick={handleClearDoneTasks}
          >
            Clear Done Items
          </button>
        </div>
      </div>

      <div className="tab-strip">
        <button
          className={`tab-btn ${activeTab === 'work' ? 'active' : ''}`}
          onClick={() => setActiveTab('work')}
        >
          Work <span className="tab-count">{tabCounts.work}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          Personal <span className="tab-count">{tabCounts.personal}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'unassigned' ? 'active' : ''}`}
          onClick={() => setActiveTab('unassigned')}
        >
          Unassigned <span className="tab-count">{tabCounts.unassigned}</span>
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="streams-container">
          {filteredStreams.length === 0 && (
            <div className="empty-tab-message">
              No streams in this tab yet. Edit a stream to assign it to "{activeTab}".
            </div>
          )}
          {filteredStreams.map(stream => (
            <Stream
              key={stream.id}
              stream={stream}
              tasks={tasks.filter(task => task.streamId === stream.id)}
              onAddTask={handleAddTask}
              onEditStream={handleEditStream}
              onDeleteTask={handleDeleteTask}
              onUpdateTask={handleUpdateTask}
              onDeleteStream={handleDeleteStream}
            />
          ))}
        </div>
      </DragDropContext>

      {showStreamForm && (
        <StreamForm
          onSubmit={editingStream ? handleUpdateStream : handleCreateStream}
          onCancel={handleCloseStreamForm}
          initialData={editingStream || undefined}
          isEditing={!!editingStream}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <MainContent />
    </Router>
  );
}

export default App;